// El hilo de una fila de GET /facility-dashboard (ADR-0134). Desde la
// Fase 2 cada `entries[]` trae los campos del hilo —estado, la FASE que
// deriva el engine, reaperturas, overlay de cierre, vencimiento— y las
// columnas de delegación quedan sólo como historia: nadie las escribe ya,
// así que leerlas congelaría la lista, «Estado hoy», la cola de la tienda
// y las métricas de proveedor en el último valor que dejó la delegación.
//
// Todo lector del tablero de facility pasa por acá: el estado pintado es
// `presentationalState` (fase + overlay → kind, etiqueta de la receta), y
// «abierta» es «no cerrada» por la misma fase, nunca una comparación de
// claves (fitness observation-phase-single-source).

import type { FacilityDashboardEntry, ObservationPhaseVocabulary } from "@bitacorapro/api-types";
import { presentationalState, type PresentationalState } from "./phase";
import { ST_PILL_CLASS } from "./pills";

type EntryThread = Pick<
  FacilityDashboardEntry,
  | "observationStatus"
  | "observationPhase"
  | "reopenCount"
  | "observationClosedAt"
  | "observationClosedReason"
>;

export function entryState(
  e: EntryThread,
  vocab?: ObservationPhaseVocabulary
): PresentationalState {
  return presentationalState(
    {
      observationStatus: e.observationStatus ?? null,
      observationPhase: e.observationPhase ?? null,
      reopenCount: e.reopenCount ?? null,
      observationClosedAt: e.observationClosedAt ?? null,
      observationClosedReason: e.observationClosedReason ?? null,
    },
    vocab
  );
}

/** Sigue abierta: ni cerrada por conformidad ni en un estado terminal del
 *  grafo (facility: «anulada»). */
export function isEntryOpen(e: EntryThread, vocab?: ObservationPhaseVocabulary): boolean {
  // ADR-0141: lo cerrado es la FASE `closed` — la conformidad o el nodo
  // terminal del grafo (la anulada de facility, que conserva kind
  // `rejected`, y la rechazada cerrada de postventa, `rejected_closed`).
  // `rejected` en espera de respuesta y `appealed` siguen ABIERTAS: sus
  // fases no son `closed`.
  return entryState(e, vocab).phase !== "closed";
}

/** La pill de estado de una fila: etiqueta de la receta + clase del tono. */
export function entryEstadoPill(
  e: EntryThread,
  vocab?: ObservationPhaseVocabulary
): { label: string; cls: string; state: PresentationalState } {
  const state = entryState(e, vocab);
  return { label: state.label, cls: ST_PILL_CLASS[state.tone], state };
}

/** Las columnas del kanban y las opciones del filtro «Estado», por fase
 *  del hilo. Una abierta reabierta vive con las abiertas (su modificador
 *  «Reabierta ×N» la distingue). */
export const ESTADO_COLUMNS = [
  { key: "abiertas", kinds: ["open", "reopened", "accepted"] },
  { key: "asignadas", kinds: ["assigned"] },
  { key: "resueltas", kinds: ["resolved"] },
  // El título sale del último kind: «Cerrada» (conforme).
  { key: "cerradas", kinds: ["closed_no_response", "conforme"] },
  // ADR-0141: el cierre del grafo es `rejected_closed` (la anulada de
  // facility). `rejected` (rechazo esperando respuesta) y `appealed` no
  // existen en el grafo de facility, pero se listan para que una fila
  // así jamás caiga al respaldo «abiertas». El título sale del último
  // kind (rejected_closed → la etiqueta del cierre: «Anulada»).
  { key: "anuladas", kinds: ["rejected", "appealed", "rejected_closed"] },
] as const satisfies ReadonlyArray<{
  key: string;
  kinds: ReadonlyArray<PresentationalState["kind"]>;
}>;

export type EstadoColumnKey = (typeof ESTADO_COLUMNS)[number]["key"];

export function columnOf(state: PresentationalState): EstadoColumnKey {
  return (
    ESTADO_COLUMNS.find((c) => (c.kinds as readonly string[]).includes(state.kind))?.key ??
    "abiertas"
  );
}

/** El título de una columna: la etiqueta de la receta de su primer kind. */
export function columnTitle(key: EstadoColumnKey, vocab?: ObservationPhaseVocabulary): string {
  const col = ESTADO_COLUMNS.find((c) => c.key === key)!;
  const kind = col.kinds[col.kinds.length - 1]!;
  return presentationalStateForKind(kind, vocab);
}

function presentationalStateForKind(
  kind: PresentationalState["kind"],
  vocab: ObservationPhaseVocabulary | undefined
): string {
  // La etiqueta sale del mismo camino que pinta las filas: una fila
  // sintética en la fase de ese kind.
  const phase =
    kind === "assigned"
      ? "with_assignee"
      : kind === "resolved"
        ? "awaiting_receiver"
        : kind === "conforme" || kind === "closed_no_response" || kind === "rejected_closed"
          ? "closed"
          : kind === "rejected"
            ? "awaiting_receiver_on_rejection"
            : kind === "accepted"
              ? "awaiting_assignment"
              : "triage";
  return presentationalState(
    {
      observationStatus: null,
      observationPhase: phase,
      reopenCount: 0,
      observationClosedAt: kind === "conforme" ? "1970-01-01T00:00:00.000Z" : null,
    },
    vocab
  ).label;
}
